import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useTheme } from '../contexts/ThemeContext';

const FourPieCharts = ({ data }) => {
    const chartRef = useRef(null);
    const { theme } = useTheme();


    useEffect(() => {
        if (!data || data.length === 0) return;


        const isDark = theme === 'dark';
        const axisColor = isDark ? '#ccc' : '#333';

        const chart = echarts.init(chartRef.current);

        // Filter out invalid channels
        data = data.filter(item => item.channels && item.channels.trim() !== '');

        // Extract channel names
        const channels = [...new Set(data.map(item => item.channels))];

        // Helper to generate pie data for a metric
        const getPieData = (metricKey) =>
            data.map(item => ({
                name: item.channels,
                value: Math.round(item[metricKey] || 0)
            })).filter(d => d.value > 0);

        const pieSeries = [
            {
                name: 'First Click',
                data: getPieData('attribution_first_click_heuristic'),
                center: ['20%', '30%']
            },
            {
                name: 'Last Click',
                data: getPieData('attribution_last_click_heuristic'),
                center: ['70%', '30%']
            },
            {
                name: 'Data Attribution 1',
                data: getPieData('attribution_markov_algorithmic'),
                center: ['20%', '75%']
            },
            {
                name: 'Data Attribution 2',
                data: getPieData('attribution_shapley_size4_conv_rate_algorithmic'),
                center: ['70%', '75%']
            }
        ];

        const series = pieSeries.map((pie, index) => ({
            type: 'pie',
            name: pie.name,
            radius: ['25%', '40%'],
            center: pie.center,
            label: {
                show: false,
                position: 'center'
            },
            emphasis: {
                label: {
                    show: true,
                    formatter: '{b}\n${c}',
                    fontSize: 16,
                    fontWeight: 'bold',
                    color: axisColor
                }
            },
            labelLine: {
                show: false
            },
            data: pie.data
        }));


        const option = {
            title: [
                { text: 'First Click', left: '10%', top: '5%', textStyle: { fontSize: 14, color: axisColor } },
                { text: 'Last Click', left: '60%', top: '5%', textStyle: { fontSize: 14, color: axisColor } },
                { text: 'Data Attribution 1', left: '10%', top: '50%', textStyle: { fontSize: 14, color: axisColor } },
                { text: 'Data Attribution 2', left: '60%', top: '50%', textStyle: { fontSize: 14, color: axisColor } },
            ],
            tooltip: {
                trigger: 'item',
                formatter: '{a} <br/>{b}: {c} ({d}%)'
            },
            legend: {
                type: 'scroll',
                orient: 'vertical',
                right: 10,
                top: 'center',
                data: channels,
                textStyle: { fontSize: 14, color: axisColor }
            },
            color: [
                '#e6194B', // strong red
                '#3cb44b', // vivid green
                '#ffe119', // bright yellow
                '#4363d8', // deep blue
                '#f58231', // orange
                '#911eb4', // strong purple
                '#46f0f0', // cyan
                '#f032e6', // magenta
                '#bcf60c', // lime
                '#fabebe', // pink
                '#008080', // teal
                '#e6beff', // lavender
                '#9A6324', // brown
                '#fffac8', // light yellow
                '#800000', // maroon
                '#aaffc3', // mint
                '#808000', // olive
                '#ffd8b1', // peach
                '#000075', // navy
                '#808080'  // gray
            ],

            series
        };

        chart.setOption(option);

        const resize = () => chart.resize();
        window.addEventListener('resize', resize);

        return () => {
            chart.dispose();
            window.removeEventListener('resize', resize);
        };
    }, [data, theme]);

    return <div ref={chartRef} style={{ width: '100%', height: '600px' }} />;
};

export default FourPieCharts;
